import {
  Autocomplete,
  Box,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useDebugPairs } from '../../../api/useUnwindDebug';
import {
  TraceCategory,
  TraceLevel,
} from '../../../types/unwind-debug.types';

const ALL_LEVELS: TraceLevel[] = ['debug', 'info', 'warn', 'error'];
const ALL_CATEGORIES: TraceCategory[] = [
  'orderbook',
  'evaluation',
  'simulation',
  'order',
  'fill',
  'rule',
  'recovery',
  'skip',
  'error',
  'heartbeat',
];

const RANGE_OPTIONS: { label: string; value: string; ms: number | null }[] = [
  { label: '15m',  value: '15m',  ms: 15 * 60_000 },
  { label: '1h',   value: '1h',   ms: 60 * 60_000 },
  { label: '6h',   value: '6h',   ms: 6 * 60 * 60_000 },
  { label: '24h',  value: '24h',  ms: 24 * 60 * 60_000 },
  { label: '7d',   value: '7d',   ms: 7 * 24 * 60 * 60_000 },
];

export interface DebugFiltersValue {
  pairId: string | null;
  ruleId: string;
  level: TraceLevel[];
  category: TraceCategory[]; // traces only
  q: string;
  range: string;
}

export interface DebugFiltersProps {
  value: DebugFiltersValue;
  onChange: (v: DebugFiltersValue) => void;
  liveTail: boolean;
  onLiveTailChange: (v: boolean) => void;
  showCategoryFilter?: boolean;
}

export function DebugFilters(props: DebugFiltersProps) {
  const { value, onChange, liveTail, onLiveTailChange, showCategoryFilter } = props;
  const { data: pairs = [] } = useDebugPairs();

  const selectedPair = pairs.find((p) => p.pairId === value.pairId) ?? null;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ flexWrap: 'wrap', rowGap: 1 }}
    >
      <Autocomplete
        size="small"
        sx={{ minWidth: 280 }}
        options={pairs}
        value={selectedPair}
        getOptionLabel={(o) => o.pairName ?? o.pairId}
        onChange={(_, opt) => onChange({ ...value, pairId: opt?.pairId ?? null })}
        renderInput={(params) => <TextField {...params} label="Pair" placeholder="All pairs" />}
        clearOnBlur={false}
      />

      <TextField
        size="small"
        label="Rule ID"
        value={value.ruleId}
        onChange={(e) => onChange({ ...value, ruleId: e.target.value })}
        sx={{ width: 220 }}
      />

      <Select
        size="small"
        multiple
        displayEmpty
        value={value.level}
        onChange={(e) =>
          onChange({ ...value, level: e.target.value as TraceLevel[] })
        }
        renderValue={(v) => (Array.isArray(v) && v.length ? v.join(',') : 'level')}
        sx={{ minWidth: 120 }}
      >
        {ALL_LEVELS.map((l) => (
          <MenuItem key={l} value={l}>{l}</MenuItem>
        ))}
      </Select>

      {showCategoryFilter && (
        <Select
          size="small"
          multiple
          displayEmpty
          value={value.category}
          onChange={(e) =>
            onChange({ ...value, category: e.target.value as TraceCategory[] })
          }
          renderValue={(v) =>
            Array.isArray(v) && v.length ? v.join(',') : 'category'
          }
          sx={{ minWidth: 140 }}
        >
          {ALL_CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>
      )}

      <TextField
        size="small"
        label="Search"
        placeholder="text in message"
        value={value.q}
        onChange={(e) => onChange({ ...value, q: e.target.value })}
        sx={{ width: 220 }}
      />

      <ToggleButtonGroup
        size="small"
        exclusive
        value={value.range}
        onChange={(_, v) => v && onChange({ ...value, range: v })}
      >
        {RANGE_OPTIONS.map((r) => (
          <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ flex: 1 }} />

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={liveTail}
            onChange={(e) => onLiveTailChange(e.target.checked)}
          />
        }
        label="Live tail"
      />
    </Stack>
  );
}

export function rangeToSinceIso(rangeValue: string): string | undefined {
  const opt = RANGE_OPTIONS.find((r) => r.value === rangeValue);
  if (!opt || opt.ms == null) return undefined;
  return new Date(Date.now() - opt.ms).toISOString();
}
